// Enhanced FullWorkoutHistory.tsx
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../../firebase";
import { useAuth } from "../../../components/authContext";
import Dropdown from "../../../components/dropdown";
import StyledButton from "../../components/button";
import ExerciseDropdown from "../components/exerciseblock";
import CalendarHeatmap from "./calendar";
import LineChartComponent from "./linechart"; // create separately
import WorkoutExerciseDisplay from "./workout-exercise-display";

interface LiftSet {
  reps: number;
  weight: number;
  rpe: number;
}

interface RunSet {
  distance: number;
  pace: number;
  time: number;
}

type WorkoutSet = LiftSet | RunSet;

interface WorkoutExercise {
  name: string;
  mode: "lift" | "run";
  sets: WorkoutSet[];
}

interface WorkoutLog {
  createdAt: { seconds: number };
  exercises: WorkoutExercise[];
}

const WorkoutHistory2: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<WorkoutLog[]>([]);
  const [groupedLogs, setGroupedLogs] = useState<Record<string, WorkoutLog[]>>(
    {}
  );
  const [liftBests, setLiftBests] = useState<
    Record<string, { weight: number; reps: number }>
  >({});
  const [topRun, setTopRun] = useState<{
    distance: number;
    pace: number;
    time: number;
  }>({ distance: 0, pace: 0, time: 0 });
  const [selectedExercise, setSelectedExercise] = useState<string>("");
  const [exerciseMode] = useState<"lift" | "run">("lift");

  useEffect(() => {
    if (!user) return;

    const fetchLogs = async () => {
      const q = query(
        collection(db, `users/${user.uid}/workouts`),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((doc) => doc.data() as WorkoutLog);
      setLogs(items);

      const grouped: Record<string, WorkoutLog[]> = {};
      const liftPB: Record<string, { weight: number; reps: number }> = {};
      let runPB = { distance: 0, pace: 0, time: 0 };

      items.forEach((log) => {
        const dateStr = new Date(log.createdAt.seconds * 1000).toDateString();
        if (!grouped[dateStr]) grouped[dateStr] = [];
        grouped[dateStr].push(log);

        log.exercises.forEach((ex) => {
          if (ex.mode === "lift") {
            ex.sets.forEach((set) => {
              const lift = set as LiftSet;
              const current = liftPB[ex.name];
              if (
                !current ||
                lift.weight * lift.reps > current.weight * current.reps
              ) {
                liftPB[ex.name] = { weight: lift.weight, reps: lift.reps };
              }
            });
          } else {
            ex.sets.forEach((set) => {
              const run = set as RunSet;
              runPB = {
                distance: Math.max(runPB.distance, run.distance),
                pace: Math.max(runPB.pace, run.pace),
                time: Math.max(runPB.time, run.time),
              };
            });
          }
        });
      });

      setGroupedLogs(grouped);
      setLiftBests(liftPB);
      setTopRun(runPB);
    };

    fetchLogs();
  }, [user]);

  const formatPace = (pace: number) =>
    `${Math.floor(pace)}:${String(Math.round((pace % 1) * 60)).padStart(
      2,
      "0"
    )} min/mile`;
  const formatTime = (minutes: number) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.round((minutes - hrs * 60 - mins) * 60);
    return `${hrs}h ${mins}m ${secs}s`;
  };

  if (!user)
    return (
      <p style={{ padding: 20 }}>Please log in to view workout history.</p>
    );

  return (
    <div style={{ padding: "20px" }}>
      <h2>Full Workout History</h2>
      <p>Welcome, {user.displayName}</p>

      <h3>Total Workouts: {logs.length}</h3>

      <h3>🏋️ Personal Records</h3>
      <ul>
        {Object.entries(liftBests).map(([name, best]) => (
          <li key={name}>
            <strong>{name}</strong>: {best.reps} reps @ {best.weight} lbs
          </li>
        ))}
        <li>Top Run Distance: {topRun.distance.toFixed(1)} mi</li>
        <li>Top Run Pace: {formatPace(topRun.pace)}</li>
        <li>Top Run Time: {formatTime(topRun.time)}</li>
      </ul>

      <CalendarHeatmap
        workoutTimestamps={logs.map(
          (log) => new Date(log.createdAt.seconds * 1000)
        )}
      />

      <h3>📈 Progress Tracker</h3>
      <ExerciseDropdown
        value={selectedExercise}
        onChange={setSelectedExercise}
        uid={user.uid}
        mode={exerciseMode}
      />
      {selectedExercise && (
        <LineChartComponent
          logs={logs}
          selectedExercise={selectedExercise}
          mode={exerciseMode}
        />
      )}

      <h3>🗓 Workouts by Date</h3>
      {Object.entries(groupedLogs).map(([date, logs], i) => (
        <Dropdown key={i} title={date}>
          {logs.map((log, j) => (
            <div key={j} style={{ marginBottom: "1rem" }}>
              {log.exercises.map((ex, k) => (
                <WorkoutExerciseDisplay key={k} exercise={ex} />
              ))}
            </div>
          ))}
        </Dropdown>
      ))}

      <StyledButton
        onClick={() => navigate("/tracker")}
        style={{ marginTop: 30 }}
      >
        ← Back to Tracker
      </StyledButton>
    </div>
  );
};

export default WorkoutHistory2;
